import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  AutoComplete,
  AutoCompleteChangeEvent,
  AutoCompleteCompleteEvent,
  ChevronIcon,
  SearchIcon,
} from '@repo/atomic-components';

type Option = { name: string; code: string };
type SelectOption = { label: string; value: string };

const toOptions = (options: string[] | SelectOption[]): Option[] =>
  options.map((o) => (typeof o === 'string' ? { name: o, code: o } : { name: o.label, code: o.value }));

interface SearchableSelectProps {
  inputId: string;
  value: string | null;
  options: string[] | SelectOption[];
  placeholder: string;
  onChange: (value: string | null) => void;
  onBlur?: () => void;
  disabled?: boolean;
  /** Marks the combobox as failing validation for assistive technology. */
  invalid?: boolean;
  /** id of the node describing the combobox, typically its validation message. */
  describedBy?: string;
  /** For multi-pick fields: the choice goes to `onChange` and the input clears. Pass `value={null}`. */
  clearOnSelect?: boolean;
  /** `small`: 12px text, and 32px tall including the border. */
  size?: 'default' | 'small';
}

export const SearchableSelect = ({
  inputId,
  value,
  options,
  placeholder,
  onChange,
  onBlur,
  disabled = false,
  invalid,
  describedBy,
  clearOnSelect = false,
  size = 'default',
}: SearchableSelectProps) => {
  const inputSizeClassName = size === 'small' ? '!h-7.5 !text-xs' : '!h-8 !text-sm';
  const allOptions = useMemo(() => toOptions(options), [options]);
  const [query, setQuery] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);
  /** Index of the option the keyboard is on; -1 while the caret is still in the text. */
  const [activeIndex, setActiveIndex] = useState(-1);
  const ref = useRef<AutoComplete>(null);
  const justSelectedRef = useRef(false);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Defer final-form `onBlur` so a mousedown on a suggestion can run `onSelect` first (blur fires before select). */
  const formBlurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearFormBlurTimeout = () => {
    if (formBlurTimeoutRef.current) {
      clearTimeout(formBlurTimeoutRef.current);
      formBlurTimeoutRef.current = null;
    }
  };

  useEffect(
    () => () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
      clearFormBlurTimeout();
    },
    [],
  );

  const getOverlayVisible = () => {
    const api = ref.current as unknown as { overlayVisible?: boolean } | null;
    return api?.overlayVisible ?? isOpen;
  };

  const selectedLabel = useMemo(() => {
    if (value === null) return '';
    const opt = allOptions.find((o) => o.code === value);
    return opt?.name ?? '';
  }, [allOptions, value]);

  const suggestions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return !needle ? allOptions : allOptions.filter((o) => o.name.toLowerCase().includes(needle));
  }, [allOptions, query]);

  const noMatch = Boolean(query.trim()) && suggestions.length === 0;

  /** PrimeReact options have no ids, so add them by index for `aria-activedescendant`. */
  const getOptionId = useCallback((index: number) => `${inputId}-option-${index}`, [inputId]);

  /**
   * `role="combobox"` needs `aria-controls`, but the popup unmounts when closed. A fixed id plus an
   * empty listbox stand in for it.
   */
  const autoCompleteId = `${inputId}-autocomplete`;
  const popupListId = `${autoCompleteId}_list`;
  const collapsedListId = `${inputId}-listbox`;

  const handleShow = () => setIsOpen(true);
  const handleHide = () => {
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const inputValue = isOpen || query ? query : selectedLabel;

  const clearBlurTimeout = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
  };

  const syncValueToQuery = useCallback(() => {
    if (value === null) return;
    // An empty query means the field was opened but nothing was typed, so the selection
    // stands: emptying the input clears the value eagerly in `onChange` instead.
    if (!query.trim()) return;
    const expected = allOptions.find((o) => o.code === value)?.name ?? '';
    if (query.trim() !== expected.trim()) {
      onChange(null);
    }
  }, [allOptions, onChange, query, value]);

  const handleBlur = () => {
    clearFormBlurTimeout();
    formBlurTimeoutRef.current = setTimeout(() => {
      formBlurTimeoutRef.current = null;
      onBlur?.();
    }, 200);

    clearBlurTimeout();
    blurTimeoutRef.current = setTimeout(() => {
      blurTimeoutRef.current = null;
      syncValueToQuery();
    }, 220);
  };

  const handleFocus = () => {
    clearBlurTimeout();
    clearFormBlurTimeout();
  };

  /** Keeps the button itself from taking focus; `toggle` hands focus to the input instead. */
  const handleToggleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  /** Opens with an empty query so the full list shows. The label comes back if closed without a choice. */
  const openWithFullList = () => {
    setQuery('');
    setIsOpen(true);
    (ref.current as unknown as { show?: () => void } | null)?.show?.();
  };

  const closePanel = () => {
    (ref.current as unknown as { hide?: () => void } | null)?.hide?.();
    setIsOpen(false);
    setActiveIndex(-1);
  };

  /** Applies a choice from a click, Enter or Tab. */
  const selectOption = (option: Option) => {
    clearBlurTimeout();
    clearFormBlurTimeout();
    // A cleared query doesn't reopen the panel, so don't set the flag — it would swallow the next keystroke.
    justSelectedRef.current = !clearOnSelect;
    setQuery(clearOnSelect ? '' : option.name);
    onChange(option.code);
    // Close explicitly — PrimeReact only closes on click, and `onHide` fires late.
    closePanel();
    queueMicrotask(() => {
      onBlur?.();
    });
  };

  /** Opens from the keyboard with an option active: the current value, or the first/last option. */
  const openFromKeyboard = (from: 'first' | 'last') => {
    if (!allOptions.length) return;

    const selectedIndex = value === null ? -1 : allOptions.findIndex((o) => o.code === value);

    openWithFullList();
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : from === 'first' ? 0 : allOptions.length - 1);
  };

  /** Walks the open list, wrapping around both ends so a long list is never a dead end. */
  const moveActiveOption = (step: number) => {
    setActiveIndex((current) => {
      if (!suggestions.length) return -1;
      if (current === -1) return step > 0 ? 0 : suggestions.length - 1;
      return (current + step + suggestions.length) % suggestions.length;
    });
  };

  /**
   * PrimeReact's key handling doesn't work (it reads `event.which`), so we handle keys ourselves in
   * the capture phase using the WAI-ARIA combobox pattern.
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const input = (ref.current as unknown as { getInput?: () => HTMLInputElement | null } | null)?.getInput?.();

    // The chevron is inside this container too, and keeps its native Enter / Space click.
    if (disabled || (input && e.target !== input)) return;

    const isPanelOpen = getOverlayVisible();

    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        e.preventDefault();
        e.stopPropagation();
        if (isPanelOpen) {
          moveActiveOption(e.key === 'ArrowDown' ? 1 : -1);
        } else {
          openFromKeyboard(e.key === 'ArrowDown' ? 'first' : 'last');
        }
        break;
      }
      case 'Home':
      case 'End': {
        // Only while the box is empty: over typed text these keys belong to the caret.
        if (!isPanelOpen || query || !suggestions.length) return;
        e.preventDefault();
        e.stopPropagation();
        setActiveIndex(e.key === 'Home' ? 0 : suggestions.length - 1);
        break;
      }
      case 'Enter': {
        if (!isPanelOpen) return;
        // Even with nothing to pick, Enter must not reach the surrounding form: submitting
        // it from an open dropdown is never what the keypress was aimed at.
        e.preventDefault();
        e.stopPropagation();
        const option = suggestions[activeIndex];
        if (option) selectOption(option);
        break;
      }
      case 'Escape': {
        if (!isPanelOpen) return;
        e.preventDefault();
        e.stopPropagation();
        closePanel();
        break;
      }
      case 'Tab': {
        if (!isPanelOpen) return;
        // Tab also moves through the list, but doesn't wrap — past either end it leaves the field.
        const next = activeIndex + (e.shiftKey ? -1 : 1);
        if (next >= 0 && next < suggestions.length) {
          e.preventDefault();
          e.stopPropagation();
          setActiveIndex(next);
          break;
        }
        // Focus is leaving: close the panel and take the active option, like Enter.
        e.stopPropagation();
        const option = suggestions[activeIndex];
        if (option) selectOption(option);
        else closePanel();
        break;
      }
    }
  };

  /**
   * Let the click bubble — PrimeReact closes other overlays on `window`. The button doesn't take
   * focus, so focus the input manually.
   */
  const toggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (disabled) return;

    ref.current?.focus();

    if (getOverlayVisible()) {
      closePanel();
      return;
    }

    openWithFullList();
  };

  /** Clicking a field that already holds a selection reopens the full list, same as the chevron. */
  const handleInputClick = () => {
    if (disabled || value === null || getOverlayVisible()) return;
    openWithFullList();
  };

  const showPlaceholderStyle = !inputValue;

  // PrimeReact only opens the overlay when `suggestions` change while `searchingState` is
  // true; the first keystroke can beat that, leaving the panel closed until the next one.
  useLayoutEffect(() => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    const q = query.trim();
    if (!q) return;
    if (value !== null && q === selectedLabel.trim()) return;
    const api = ref.current as { show?: () => void } | null;
    queueMicrotask(() => api?.show?.());
  }, [query, selectedLabel, value]);

  /** Options are portalled, so apply `aria-activedescendant` and the highlight once they're in the DOM. */
  useEffect(() => {
    const overlay = (ref.current as unknown as { getOverlay?: () => HTMLElement | null } | null)?.getOverlay?.();
    if (!overlay) return;

    overlay.querySelectorAll<HTMLLIElement>('li[role="option"]').forEach((item, index) => {
      item.id = getOptionId(index);
      const isActive = index === activeIndex;
      item.setAttribute('data-p-highlight', String(isActive));

      if (!isActive) return;

      // Scroll within the panel only: `scrollIntoView` would drag the form behind it too.
      const top = item.offsetTop;
      const bottom = top + item.offsetHeight;
      if (top < overlay.scrollTop) overlay.scrollTop = top;
      else if (bottom > overlay.scrollTop + overlay.clientHeight) overlay.scrollTop = bottom - overlay.clientHeight;
    });
  }, [activeIndex, getOptionId, isOpen, suggestions]);

  return (
    <div className="relative w-full" onKeyDownCapture={handleKeyDown}>
      <div className="border-theme-border-grey bg-theme-white relative flex w-full items-stretch overflow-hidden rounded-sm border">
        {/* Overlaid, so the input the option panel aligns to spans the whole field. */}
        <div className="text-theme-mid-grey pointer-events-none absolute inset-y-0 left-0 z-[1] flex w-8 items-center justify-center">
          <SearchIcon width={16} height={16} aria-hidden />
        </div>

        <div className="relative min-w-0 flex-1">
          {/* PrimeReact forwards any `aria-*` prop it does not own straight to the inner input. */}
          <AutoComplete
            ref={ref}
            id={autoCompleteId}
            inputId={inputId}
            disabled={disabled}
            aria-controls={isOpen ? popupListId : collapsedListId}
            aria-invalid={invalid}
            aria-describedby={describedBy}
            aria-activedescendant={isOpen && activeIndex >= 0 ? getOptionId(activeIndex) : undefined}
            value={inputValue}
            suggestions={suggestions}
            completeMethod={(e: AutoCompleteCompleteEvent) => setQuery(e.query ?? '')}
            delay={0}
            minLength={1}
            loadingIcon={<></>}
            field="name"
            dropdown={false}
            showEmptyMessage={noMatch}
            emptyMessage={`No match '${query.trim()}'`}
            onShow={handleShow}
            onHide={handleHide}
            placeholder={placeholder}
            onChange={(e: AutoCompleteChangeEvent) => {
              if (typeof e.value === 'string') {
                if (!isOpen) setIsOpen(true);
                setQuery(e.value);
                // A new query is a new list: nothing is navigated to until the user arrows again.
                setActiveIndex(-1);
                if (!e.value) {
                  onChange(null);
                  closePanel();
                }
                return;
              }
            }}
            onSelect={(e) => {
              selectOption(e.value as Option);
            }}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onClick={handleInputClick}
            className="w-full"
            inputClassName={[
              `${inputSizeClassName} !w-full !rounded-none !border-0 !bg-theme-white !shadow-none !outline-none focus:!ring-0`,
              showPlaceholderStyle ? 'placeholder:!text-theme-text-grey placeholder:!leading-5' : '',
              !showPlaceholderStyle ? '!text-theme-black' : '',
              size === 'small' ? 'placeholder:!text-xs' : 'placeholder:!text-sm',
              '!pr-11 !py-[6px] !pl-[33px]',
            ].join(' ')}
            pt={{
              // Unstyled PrimeReact marks its highlighted option with the attribute alone, so
              // without this the keyboard walks a list that shows no sign of where it is.
              panel: { className: '!bg-theme-white !text-theme-black !border !border-theme-border-grey' },
              item: {
                className:
                  '!text-theme-black hover:!bg-theme-background-grey data-[p-highlight=true]:!bg-theme-border-grey',
              },
              emptyMessage: {
                className:
                  '!m-0 !flex !cursor-default !items-center !justify-center !border-0 !bg-theme-border-grey !py-2 !text-sm !text-theme-black',
              },
              dropdownButton: {
                root: {
                  'aria-label': 'Toggle options',
                },
              },
            }}
          />

          {/* The stand-in popup `aria-controls` points at while the real one is unmounted. */}
          <ul id={collapsedListId} role="listbox" hidden />
        </div>

        <button
          type="button"
          aria-label="Toggle options"
          disabled={disabled}
          className="bg-theme-white absolute inset-y-0 right-0 z-[1] flex w-[40px] cursor-pointer items-center justify-center disabled:cursor-default disabled:opacity-60"
          onMouseDown={handleToggleMouseDown}
          onClick={toggle}
          // Tabbing here from the input stays in the combobox, so cancel the pending blur validation.
          onFocus={handleFocus}
          onBlur={handleBlur}
        >
          <ChevronIcon
            width={8}
            height={5}
            className={[
              'transition-transform duration-150 ease-in-out',
              isOpen ? 'rotate-180' : 'rotate-0',
              '[&>path]:!stroke-[#13898D]',
            ].join(' ')}
          />
        </button>
      </div>
    </div>
  );
};
